<aside class="alerts-drawer">
    <!-- The card stretches to the height of the list beside it, so the two end level.
         This holds the content at the top of the viewport as that list scrolls past,
         rather than letting it scroll away and leave the pane empty. -->
    <div class="alerts-drawer-sticky">
        <div class="alerts-drawer-header">
            <div class="alerts-drawer-heading">
                <span class="alerts-drawer-eyebrow">Alert Detail</span>
                <span class="alerts-drawer-title-row">
                    <span class="alerts-drawer-title" [title]="title">{{ title }}</span>
                    <!-- The raw id beside the readable name. It is what a mute names and what
                         a ticket quotes, so it belongs where the reader already is rather
                         than only on Advanced - faded and monospaced so it reads as the
                         machine name for the line beside it, not as a second heading. -->
                    @if (alertTypeId) {
                    <span class="alerts-drawer-type-id" [title]="alertTypeId">{{ alertTypeId }}</span>
                    }
                </span>
            </div>
            <div class="alerts-drawer-actions">
                @if (total > 1) {
                <!-- Pages through the alerts currently loaded in the table, in the order
                     they are shown, so the drawer follows whatever the reader filtered to. -->
                <div class="alerts-drawer-pager">
                    <button
                        type="button"
                        class="alerts-pager-btn"
                        id="AlertDrawerPrev"
                        [disabled]="!hasPrev"
                        (click)="navigate(-1)"
                        title="Previous alert"
                        aria-label="Previous alert"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.25"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                    <span class="alerts-pager-count"
                        >{{ index + 1 }}<span class="alerts-pager-sep">/</span
                        ><span class="alerts-pager-total">{{ total }}</span></span
                    >
                    <button
                        type="button"
                        class="alerts-pager-btn"
                        id="AlertDrawerNext"
                        [disabled]="!hasNext"
                        (click)="navigate(1)"
                        title="Next alert"
                        aria-label="Next alert"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.25"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                        >
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>
                </div>
                }
                <!-- Not rendered at all while the AI assistant is off for this network. -->
                @if (alert && assistant.available()) {
                <button
                    type="button"
                    class="alerts-pager-btn alerts-pager-btn--standalone"
                    id="AlertDrawerExplain"
                    [disabled]="assistant.isStreaming()"
                    (click)="explainWithAssistant()"
                    [title]="assistant.isStreaming() ? 'The AI assistant is still answering' : 'Ask the AI assistant to explain this alert'"
                    aria-label="Ask the AI assistant to explain this alert"
                >
                    <span class="pi pi-sparkles" aria-hidden="true"></span>
                </button>
                }
                <!-- One button for every pane. None of them has a timer behind it: the
                     listing polls itself, but the history and the policy sit outside that
                     read, so this is the only way to ask either for a fresh answer. -->
                <button
                    type="button"
                    class="alerts-pager-btn alerts-pager-btn--standalone"
                    id="AlertDrawerRefresh"
                    [disabled]="refreshing"
                    (click)="refresh()"
                    [title]="refreshTitle"
                    aria-label="Reload"
                >
                    <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                </button>
                <button
                    type="button"
                    class="alerts-drawer-close"
                    id="AlertDrawerClose"
                    (click)="close()"
                    aria-label="Close"
                >
                    &times;
                </button>
            </div>
        </div>

        <!-- Triage actions: claim / resolve / mute, kept at the top by the alert identity (rather
             than a footer marooned at the bottom of a full-height drawer). Shown only when the host
             asks for it — the Alerts page leaves it off. A button appears only where it applies. -->
        @if (showActions && alert) {
        <div class="alerts-drawer-actionbar">
            <div class="alerts-drawer-actionbar-group">
                @if (alertClaimed) {
                <button type="button" class="adr-action" id="AlertDrawerRelease" [disabled]="saving" (click)="releaseAction()">
                    <span class="pi pi-user-minus" aria-hidden="true"></span>Release
                </button>
                } @else if (canClaim) {
                <button
                    type="button"
                    class="adr-action adr-action--primary"
                    id="AlertDrawerClaim"
                    [disabled]="saving"
                    (click)="claimAction()"
                >
                    <span class="pi pi-user-plus" aria-hidden="true"></span>Claim
                </button>
                }
                @if (canResolve) {
                <button type="button" class="adr-action" id="AlertDrawerResolve" [disabled]="saving" (click)="resolveAction()">
                    <span class="pi pi-check" aria-hidden="true"></span>Resolve
                </button>
                }
                @if (canMuteAlert) {
                <button type="button" class="adr-action" id="AlertDrawerMute" [disabled]="saving" (click)="muteAction()">
                    <span class="pi pi-bell-slash" aria-hidden="true"></span>Mute
                </button>
                }
            </div>
            <!-- The full page for what the drawer does not: network mute, related alerts, filters. -->
            @if (showOpenInAlerts) {
            <a
                class="adr-open-link"
                id="AlertDrawerOpenInAlerts"
                [routerLink]="alertsPageRoute"
                [queryParams]="{ alert: alert.id }"
                (click)="close()"
            >
                Open in Alerts
            </a>
            }
        </div>
        }

        <!-- Panes over one alert, split by the question each answers rather than by field
             type. History and Policy are the two whose content is a second request, and each
             is read the first time somebody asks for it. Policy is offered only where there
             is a policy to show and the reader may read it. -->
        <div class="alerts-drawer-tabs" role="tablist" aria-label="Alert detail">
            @for (pane of tabs; track pane.id) {
            <button
                type="button"
                class="alerts-drawer-tab"
                id="AlertDrawerTab{{ pane.id }}"
                role="tab"
                [class.selected]="tab === pane.id"
                [attr.aria-selected]="tab === pane.id"
                (click)="setTab(pane.id)"
            >
                {{ pane.label }}
            </button>
            }
        </div>

        <div class="alerts-drawer-body">
            @if (tab === 'policy') { @if (policyError) {
            <!-- On the tab for the same reason the history's error is: it describes the one
                 thing this pane shows, and every other pane is already correct without it. -->
            <div class="alerts-drawer-note alerts-drawer-note--error" id="AlertDrawerPolicyError" role="alert">
                {{ policyError }}
            </div>
            } @else if (loadingPolicy) {
            <div class="alerts-drawer-note" id="AlertDrawerPolicyLoading">Loading the policy…</div>
            } @else {
            <!-- Said before the numbers. The thresholds below are what the *next*
                 measurement will be graded against, which is not always what graded this
                 alert: a policy can be moved, or replaced by a narrower one, after an alert
                 opens and nothing is written to the alert when it is. -->
            <div class="alerts-drawer-note" id="AlertDrawerPolicyNote">{{ policyNote }}</div>
            <ng-container [ngTemplateOutlet]="sectionTables"></ng-container>
            } } @else if (tab === 'history') { @if (historyError) {
            <!-- On the tab rather than in a growler: it describes the one thing this pane
                 shows, and the Details pane beside it is already correct without it. -->
            <div class="alerts-drawer-note alerts-drawer-note--error" id="AlertDrawerHistoryError" role="alert">
                {{ historyError }}
            </div>
            } @else if (loadingHistory) {
            <div class="alerts-drawer-note" id="AlertDrawerHistoryLoading">Loading the history…</div>
            } @else {
            <!-- Said before the events, not after them. A derived timeline is strictly less
                 than a recorded one - no severity changes, no freezes, no releases
                 - so an alert somebody took and handed back looks here like one nobody
                 touched. A reader has to know that before they read down the list. -->
            @if (derived) {
            <div class="alerts-drawer-note alerts-drawer-note--warn" id="AlertDrawerHistoryDerived">
                No history was recorded for this alert, so this timeline is built from the alert itself. Anything that
                left no mark is missing from it: severity changes, freezes, and any claim that was handed back.
            </div>
            } @if (events.length === 0) {
            <div class="alerts-drawer-note" id="AlertDrawerHistoryEmpty">Nothing has happened to this alert yet.</div>
            } @else {
            <!-- The lifecycle as a vertical timeline: a rail links the events in the order the
                 endpoint sent them (it orders by time; `sequence` is not monotonic with it), and
                 each node carries what happened and its time, with the actor and detail beneath
                 when there are any. Reads as a story rather than a grid of cells. -->
            <ol class="adr-timeline" id="AlertDrawerHistory" aria-label="Alert history">
                @for (event of events; track event.id) {
                <li class="adr-tl">
                    <span class="adr-tl__marker" aria-hidden="true">
                        <span class="adr-tl__dot" [attr.data-kind]="event.type"></span>
                    </span>
                    <div class="adr-tl__body">
                        <div class="adr-tl__head">
                            <span class="adr-tl__event">{{ eventText(event) }}</span>
                            <span class="adr-tl__when">{{ eventTime(event) }}</span>
                        </div>
                        <!-- Actor only when someone is named — a detector or the sweeper leaves it
                             blank, and the head above carries the node on its own. -->
                        @if (eventActorCell(event)) {
                        <div class="adr-tl__by">{{ eventActorCell(event) }}</div>
                        }
                        @if (eventDetailCell(event)) {
                        <div class="adr-tl__detail">{{ eventDetailCell(event) }}</div>
                        }
                    </div>
                </li>
                }
            </ol>
            } } } @else if (tab === 'details') {
            <!-- Details: an "ops board" triage view — a readout strip (severity / status / age / scope)
                 mirroring the dashboard tiles, then a hairline list of what's affected. -->
            <div class="adr-detail">
                <div class="adr-strip" [attr.data-sev]="severityKey">
                    <div class="adr-cell">
                        <div class="adr-cell__lab">Severity</div>
                        <div class="adr-cell__sev">{{ severityLabel }}</div>
                    </div>
                    <div class="adr-cell">
                        <div class="adr-cell__lab">Status</div>
                        <div class="adr-cell__val">
                            <span class="adr-dot" [attr.data-status]="statusKey" aria-hidden="true"></span>{{ statusLabel }}
                        </div>
                    </div>
                    <div class="adr-cell">
                        <div class="adr-cell__lab">Age</div>
                        <div class="adr-cell__val adr-num">{{ ageText }}</div>
                    </div>
                    <div class="adr-cell">
                        <div class="adr-cell__lab">Scope</div>
                        <div class="adr-cell__val">{{ scopeText }}</div>
                    </div>
                </div>

                @if (isStale || isMuted || alertClaimed) {
                <div class="adr-flags">
                    @if (isStale) {
                    <span class="adr-flag adr-flag--stale">Not seen recently</span>
                    }
                    @if (isMuted) {
                    <span class="adr-flag adr-flag--muted">Muted</span>
                    }
                    @if (alertClaimed) {
                    <span class="adr-flag adr-flag--claimed">Claimed</span>
                    }
                </div>
                }

                @if (detailComponents.length) {
                <div class="adr-block">
                    <div class="adr-block__label">Affected {{ detailComponents.length > 1 ? 'components' : 'component' }}</div>
                    <div class="adr-clist">
                        @for (c of detailComponents; track c.id || c.name) {
                        <a class="adr-crow" [class.adr-crow--link]="!!c.route" [routerLink]="c.route">
                            <span class="adr-cchip" [attr.data-kind]="c.kind" aria-hidden="true"><span [class]="c.iconClass"></span></span>
                            <span class="adr-cname" [title]="c.name">{{ c.name }}</span>
                            <span class="adr-cend">
                                @if (c.endLetter) {
                                <span class="adr-cend__letter">{{ c.endLetter }}</span>
                                }
                                @if (c.route) {
                                <span class="adr-cend__go" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                                </span>
                                }
                            </span>
                        </a>
                        }
                    </div>
                </div>
                }

                @if (alertClaimed) {
                <div class="adr-block">
                    <div class="adr-block__label">Ownership</div>
                    <div class="adr-owner">
                        <span class="adr-owner__avatar" aria-hidden="true">{{ claimedByName.charAt(0) }}</span>
                        <span class="adr-owner__body">
                            <span class="adr-owner__line">
                                <span class="adr-owner__name">{{ claimedByName }}</span> claimed this{{ claimedAgo ? ' ' + claimedAgo : '' }}
                            </span>
                            @if (claimNote) {
                            <span class="adr-owner__note">{{ claimNote }}</span>
                            }
                        </span>
                    </div>
                </div>
                }
            </div>
            } @else {
            <!-- Source, Muting and Advanced are built from the alert's own fields, drawn the same way. -->
            <ng-container [ngTemplateOutlet]="sectionTables"></ng-container>
            }
        </div>
    </div>
</aside>

<!-- The section table, shared by every pane built from rows.
     One definition rather than one per pane: the panes differ in which sections they
     hold, never in how a section is drawn, and two copies of this markup would be two
     things to keep in step. -->
<ng-template #sectionTables>
    @for (section of sections; track section.key) {
    <table class="alerts-detail-table" id="AlertDrawerSection{{ section.key }}" [attr.aria-label]="section.title">
        <!-- The column widths have to live here, not on the body cells. Under
                     `table-layout: fixed` the columns are sized from the first row alone, and
                     the first row is the section heading spanning both - so a width on a <td>
                     below is never read. A <colgroup> is what fixed layout does honour. -->
        <colgroup>
            <col class="alerts-detail-key-col" />
            <col />
        </colgroup>
        <thead>
            <tr>
                <th colspan="2">{{ section.title }}</th>
            </tr>
        </thead>
        <tbody>
            @for (row of section.rows; track row.key) { @if (row.table && row.table.variant === 'records' && row.table.columns.length > 1) {
            <!-- A per-entity metrics table (the latency directions): full-width framed stat blocks —
                 the readout-cell idiom the Details pane uses — with the row label as a caption above
                 them, so the measurements read as evidence rather than a flat grid. -->
            <tr id="AlertDrawerRow{{ row.key }}" class="alerts-detail-tablerow">
                <td colspan="2">
                    <div class="alerts-detail-tablecap">{{ row.label }}</div>
                    <div class="adr-measures">
                        @for (cells of row.table.rows; track $index) {
                        <div class="adr-measure">
                            <div class="adr-measure__head">
                                <span class="adr-measure__name" [class.mono]="cells[0].mono">{{ cells[0].value }}</span>
                                @if (cells[0].id) {
                                <span class="adr-measure__id">{{ cells[0].id }}</span>
                                }
                            </div>
                            <div class="adr-measure__stats">
                                @for (column of row.table.columns; track $index; let i = $index) { @if (i > 0) {
                                <div class="adr-metric">
                                    <div class="adr-metric__lab">{{ column }}</div>
                                    <div class="adr-metric__val">{{ cells[i].value }}</div>
                                </div>
                                } }
                            </div>
                        </div>
                        }
                    </div>
                </td>
            </tr>
            } @else if (row.table && row.table.variant === 'ladder') {
            <!-- An escalation ladder in the value column: one rung per row, least severe first,
                 with the rung the alert stands on marked in words as well as tone. -->
            <tr id="AlertDrawerRow{{ row.key }}">
                <td class="alerts-detail-key">{{ row.label }}</td>
                <td class="alerts-detail-value">
                    <table class="alerts-detail-subtable adr-ladder" [attr.aria-label]="row.label">
                        <thead>
                            <tr>
                                @for (column of row.table.columns; track $index) {
                                <th scope="col">{{ column }}</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            @for (cells of row.table.rows; track $index) {
                            <tr
                                class="adr-ladder__rung"
                                [class.adr-ladder__rung--current]="cells[0].current"
                                [attr.data-sev]="cells[0].severity"
                                [attr.aria-current]="cells[0].current ? 'step' : null"
                            >
                                <td>
                                    <span class="adr-ladder__sev">{{ cells[0].value }}</span>
                                    @if (cells[0].current) {
                                    <span class="adr-ladder__now">Current</span>
                                    }
                                </td>
                                <td>{{ cells[1].value }}</td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </td>
            </tr>
            } @else if (row.table && row.table.variant === 'map') {
            <!-- A name->count distribution (failure causes): each cause is a compact chip with its
                 count paired to it, not pushed to the far edge, so a cause and how often it happened
                 read as one unit. A field of this section, like the rows above it. -->
            <tr id="AlertDrawerRow{{ row.key }}">
                <td class="alerts-detail-key">{{ row.label }}</td>
                <td class="alerts-detail-value">
                    <div class="adr-tags">
                        @for (cells of row.table.rows; track $index) {
                        <span class="adr-tag">
                            <span class="adr-tag__name" [class.mono]="cells[0].mono">{{ cells[0].value }}</span>
                            @if (cells.length > 1) {
                            <span class="adr-tag__count">{{ cells[1].value }}</span>
                            }
                        </span>
                        }
                    </div>
                </td>
            </tr>
            } @else if (row.table) {
            <!-- A plain list of names (e.g. the policy's components): stacked in the value column. -->
            <tr id="AlertDrawerRow{{ row.key }}">
                <td class="alerts-detail-key">{{ row.label }}</td>
                <td class="alerts-detail-value">
                    <div class="adr-maplist">
                        @for (cells of row.table.rows; track $index) {
                        <div class="adr-maprow">
                            <span class="adr-maprow__k" [class.mono]="cells[0].mono"
                                >{{ cells[0].value }}@if (cells[0].id) {<span class="adr-maprow__kid">{{ cells[0].id }}</span>}</span
                            >
                        </div>
                        }
                    </div>
                </td>
            </tr>
            } @else {
            <tr id="AlertDrawerRow{{ row.key }}">
                <td class="alerts-detail-key">{{ row.label }}</td>
                <td class="alerts-detail-value" [class.mono]="row.mono">
                    <!-- The value, then the id it resolved from: quieter, and spaced by the
                         separator's own margins. Each is its own element, so the blanks this
                         template is wrapped with land between elements and never inside a
                         value. -->
                    <span class="alerts-detail-value-text">{{ row.value }}</span>
                    @if (row.id) {
                    <span class="alerts-detail-value-id">{{ row.id }}</span>
                    }
                </td>
            </tr>
            } }
        </tbody>
    </table>
    }
</ng-template>
